import React, { useEffect, useState, useRef, useContext } from 'react';
import * as XLSX from 'xlsx';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import { getFilter } from '../../services/filterPanel';
import { getRegionCities } from '../../services/regionApi';
import { saveSlotConfig } from '../../services/addSlot';
import CloseCircleIcon from '../../components/CloseCircleIcon';
import { useLoading } from '../../context/LoadingProvider';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  backgroundColor: 'white',
  borderRadius: 4,
  boxShadow: 24,
  p: 4,
};

const AddSlotModal = ({ open, onClose, selectedLocation }) => {
  const [filters, setFilters] = useState([]);
  const [regionList, setRegionList] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const { loading, setLoading } = useLoading();

  useEffect(() => {
    async function fetchFiltersAndPrefill() {
      try {
        const res = await getFilter();
        const filteredFilters = res.filter((f) => f.key !== 'region' );
        setFilters(filteredFilters);

        const initialValues = {};
        filteredFilters.forEach(({ key }) => {
          initialValues[key] = '';
        });

        if (selectedLocation) {
          initialValues.city = selectedLocation;
          await fetchRegionsForCity(selectedLocation);
        }

        setFormValues(initialValues);
      } catch (error) {
        console.error('Failed to fetch filters:', error);
      }
    }

    if (open) {
      fetchFiltersAndPrefill();
      clearFile();
    } else {
      setFilters([]);
      setFormValues({});
      clearFile();
      setRegionList([]);
      setLoading(false);
    }
  }, [open, selectedLocation, setLoading]);

  const fetchRegionsForCity = async (city) => {
    if (!city) return;
    try {
      const res = await getRegionCities({ city });
      if (Array.isArray(res)) {
        setRegionList(res);
      }
    } catch (error) {
      console.error('Failed to fetch regions:', error);
    }
  };

  const handleChange = (key, value) => {
    setFormValues((prev) => {
      const updated = { ...prev, [key]: value };

      if (key === 'region') {
        const regionObj = regionList.find((r) => r.regionName === value);
        updated.regionId = regionObj?.regionId || '';
      }

      return updated;
    });
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);

    if (selectedFile) {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (jsonData.length > 1) {
          const headers = jsonData[0].map((h) => h.toLowerCase());
          const firstRow = jsonData[1];

          const newFormValues = { ...formValues };

          filters.forEach(({ key }) => {
            const index = headers.indexOf(key.toLowerCase());
            if (index !== -1) newFormValues[key] = firstRow[index];
          });

          const regionIndex = headers.indexOf('region');
          if (regionIndex !== -1 && firstRow[regionIndex]) {
            newFormValues.region = firstRow[regionIndex];
            const regionObj = regionList.find((r) => r.regionName === firstRow[regionIndex]);
            newFormValues.regionId = regionObj?.regionId || '';
          }

          setFormValues(newFormValues);
        }
      };
      reader.readAsArrayBuffer(selectedFile);
    }
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const isFormValid =
    filters.every(({ key }) => formValues[key]) &&
    // formValues.regionId &&
    file;

  const handleCreateSlot = async () => {
    if (!isFormValid) return;

    setLoading(true);
    try {
      const result = await saveSlotConfig({
        vehicleType: formValues.vehicleType,
        regionId: formValues.city,  
        corporateId: formValues.corporate,
        // tripType: formValues.tripType,
        toTime: Date.now(),
        file,

      });
      if (result === true) {
        alert('Slot config saved successfully!');
        onClose();
      } else {
        alert('Unexpected API response.');
      }
    } catch (error) {
      alert('Failed to save slot config.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') onClose();
      }}
    >
      <Box sx={{ ...modalStyle, position: 'relative' }}>
        <CloseCircleIcon
          onClick={onClose}
          style={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}
        />
        <Typography variant="h6" mb={2}>Create New Slot</Typography>

        {filters.map(({ key, values }) => (
          <FormControl fullWidth size="small" sx={{ mb: 3 }} key={key}>
            <InputLabel>{key.charAt(0).toUpperCase() + key.slice(1)}</InputLabel>
            <Select
              label={key}
              value={formValues[key] || ''}
              onChange={(e) => handleChange(key, e.target.value)}
              disabled={loading}
            >
              {values.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}

        {regionList.length > 0 && (
          <FormControl fullWidth size="small" sx={{ mb: 3 }}>
            <InputLabel>Region</InputLabel>
            <Select
              label="Region"
              value={formValues.region || ''}
              onChange={(e) => handleChange('region', e.target.value)}
              disabled={loading}
            >
              {regionList.map(({ regionName }) => (
                <MenuItem key={regionName} value={regionName}>{regionName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx"
          onChange={handleFileChange}
          style={{ display: 'block', width: '100%', marginTop: 16, marginBottom: 8 }}
          disabled={loading}
        />

        {file && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                fontStyle: 'italic',
                color: 'text.secondary',
                flexGrow: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Uploaded File: {file.name}
            </Typography>
            <IconButton
              size="small"
              aria-label="Clear file"
              onClick={clearFile}
              disabled={loading}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        <Button
          variant="contained"
          fullWidth
          onClick={handleCreateSlot}
          sx={{
            backgroundColor: '#70B92F',
            '&:hover': { backgroundColor: '#5aa122' },
            marginTop: '20px'
          }}
          disabled={loading || !isFormValid}
        >
          {loading ? 'Saving...' : 'Create'}
        </Button>
      </Box>
    </Modal>
  );
};

export default AddSlotModal;